import { v4 as uuidv4 } from 'uuid';

interface QueuedTask {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: any;
  timestamp: number;
  retries: number;
}

class OfflineTaskQueue {
  private dbName = 'BotalaDB';
  private storeName = 'pendingTasks';
  private db: IDBDatabase | null = null;

  async init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };
    });
  }

  async addTask(url: string, method: string, headers: Record<string, string>, body: any) {
    if (!this.db) await this.init();

    const task: QueuedTask = {
      id: uuidv4(),
      url,
      method,
      headers,
      body,
      timestamp: Date.now(),
      retries: 0
    };

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(task);

      request.onsuccess = () => {
        // Request background sync if available
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
          navigator.serviceWorker.ready.then((registration) => {
            return (registration as any).sync.register('sync-tasks');
          });
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAllTasks(): Promise<QueuedTask[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeTask(id: string) {
    if (!this.db) await this.init();

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async syncPendingTasks() {
    const tasks = await this.getAllTasks();
    const results = [];

    for (const task of tasks) {
      try {
        const response = await fetch(task.url, {
          method: task.method,
          headers: task.headers,
          body: JSON.stringify(task.body)
        });

        if (response.ok) {
          await this.removeTask(task.id);
          results.push({ taskId: task.id, success: true });
        } else {
          results.push({ taskId: task.id, success: false, error: 'Request failed' });
        }
      } catch (error) {
        results.push({ taskId: task.id, success: false, error: error });
      }
    }

    return results;
  }
}

export const offlineQueue = new OfflineTaskQueue();

// Enhanced fetch that queues requests when offline
export async function offlineFetch(url: string, options: RequestInit = {}) {
  // Check if online
  if (!navigator.onLine) {
    // Queue the request for later
    await offlineQueue.addTask(
      url,
      options.method || 'GET',
      options.headers as Record<string, string> || {},
      options.body ? JSON.parse(options.body as string) : null
    );
    
    // Return a mock response
    return {
      ok: true,
      status: 202,
      json: async () => ({ queued: true, message: 'Task queued for sync when online' })
    };
  }

  // If online, make the request normally
  return fetch(url, options);
}

// Listen for online/offline events
window.addEventListener('online', async () => {
  console.log('Back online! Syncing pending tasks...');
  const results = await offlineQueue.syncPendingTasks();
  console.log('Sync results:', results);
});

window.addEventListener('offline', () => {
  console.log('App is offline. Tasks will be queued.');
});